import React from 'react'
import { z } from 'zod'

import { Form } from '@/app/component/form'
import { ActionsContainer } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'
import { useCreateProformaInvoice } from '@/app/module/payments/hooks/use-create-proforma-invoice'

type Props = {
  onSuccess?: () => void
}

type FormValues = z.infer<typeof schema>

const ProformaInvoice: React.FC<Props> = ({ onSuccess }) => {
  const [createProforma, { isLoading }] = useCreateProformaInvoice()

  const defaultValues = React.useMemo<FormValues>(
    () => ({
      sectionBasic: undefined,
      sectionExtra: undefined,
      name: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      amount: 0,
      date: new Date().toISOString().slice(0, 10),
      line1: '',
      line2: '',
    }),
    [],
  )

  const handleSubmit = React.useCallback(
    async (values: FormValues) => {
      const payload: Record<string, unknown> = { ...values, date: new Date(values.date).toISOString() }
      delete (payload as any).sectionBasic
      delete (payload as any).sectionExtra
      const success = await createProforma(payload as any)
      if (success && onSuccess) onSuccess()
    },
    [createProforma, onSuccess],
  )

  return (
    <Form
      defaultValues={defaultValues}
      fields={fields}
      name="proforma-invoice"
      schema={schema}
      shouldReset
      onSubmit={handleSubmit}
      renderActions={({ isDirty, isSubmitting }) => (
        <ActionsContainer>
          <SubmitButton isDirty={isDirty} isSubmitting={isSubmitting || isLoading}>
            Generate invoice
          </SubmitButton>
        </ActionsContainer>
      )}
    />
  )
}

const schema = z.object({
  name: z.string().min(1, 'Required'),
  address1: z.string().min(1, 'Required'),
  address2: z.string().optional(),
  city: z.string().min(1, 'Required'),
  state: z.string().min(1, 'Required'),
  postalCode: z.string().min(1, 'Required'),
  country: z.string().min(1, 'Required'),
  amount: z.number().min(1, 'Must be at least 1'),
  date: z.string().min(1, 'Required'),
  line1: z.string().optional(),
  line2: z.string().optional(),
})

const fields = {
  sectionBasic: {
    type: 'title',
    fieldProps: {
      name: 'sectionBasic',
      color: 'textSecondary',
      variant: 'h6',
      children: 'Basic information',
      sx: { my: 1 },
    },
  },
  name: {
    type: 'text',
    fieldProps: { name: 'name', label: 'Name', required: true, placeholder: 'John Doe', autoFocus: true },
  },
  address1: {
    type: 'text',
    fieldProps: {
      name: 'address1',
      label: 'Address 1',
      required: true,
      placeholder: '1234 Main Street',
    },
  },
  address2: {
    type: 'text',
    fieldProps: {
      name: 'address2',
      label: 'Address 2',
      required: false,
      placeholder: 'Apt 6, 4th floor',
    },
  },
  city: {
    type: 'text',
    fieldProps: { name: 'city', label: 'City', required: true, placeholder: 'Metropolis' },
  },
  state: {
    type: 'text',
    fieldProps: { name: 'state', label: 'State', required: true, placeholder: 'Kansas' },
  },
  postalCode: {
    type: 'text',
    fieldProps: { name: 'postalCode', label: 'Postal Code', required: true, placeholder: '10345' },
  },
  country: {
    type: 'text',
    fieldProps: { name: 'country', label: 'Country', required: true, placeholder: 'USA' },
  },
  amount: {
    type: 'number',
    fieldProps: { name: 'amount', label: 'Amount', required: true, placeholder: '100' },
  },
  date: {
    type: 'date',
    fieldProps: { name: 'date', label: 'Date', required: true },
  },
  sectionExtra: {
    type: 'title',
    fieldProps: {
      name: 'sectionExtra',
      color: 'textSecondary',
      variant: 'h6',
      children: 'Extra information',
      sx: { mt: 2, mb: 1 },
    },
  },
  line1: {
    type: 'text',
    fieldProps: { name: 'line1', label: 'Title', required: false, placeholder: 'Purchase Order #' },
  },
  line2: {
    type: 'text',
    fieldProps: { name: 'line2', label: 'Value', required: false, placeholder: '883' },
  },
} as const

export default ProformaInvoice
